"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zUpdateShopProfileRequest = exports.SHOP_TYPE_LABELS = exports.SHOP_TYPES = exports.PDF_TEMPLATES = exports.SUPPORTED_COUNTRY_CODES = void 0;
const zod_1 = require("zod");
/** US and Canada only for now — a shop's country also drives which
 * region-code list (US_STATE_CODES vs CA_PROVINCE_CODES, in
 * apps/web/src/lib/geo.ts) and postal-code label the address form shows,
 * and whether the Rates & Tax section's Canada-only fields (tax label
 * override, GST/HST number) are offered at all. Never printed on any
 * estimate/invoice PDF — see formatAddress() in
 * apps/api/src/common/pdf-identity.ts, which deliberately has no country
 * parameter. Add more codes here (and to CountryCode in shop-profile.ts's
 * frontend usage) as more countries are supported. */
exports.SUPPORTED_COUNTRY_CODES = ["US", "CA"];
/** Which layout a shop's invoice/estimate PDFs print in — independent of
 * the app's own UI (this app has only ever had one presentation layer
 * since the separate Classic UI theme was removed, see docs/TRAPS.md).
 * "classic" is the original Times-New-Roman, sample-invoice-matching
 * layout (see apps/api/src/pdf/invoice-template.ts) — still fully
 * supported, not deprecated; "modern" (added 2026-09-15) mirrors
 * InvoiceDetailPageModern.tsx's own visual language — see
 * apps/api/src/pdf/invoice-template-modern.ts. Owner-editable (see
 * zUpdateShopProfileRequest below and Shop Settings' own "Invoice
 * template" picker) — an admin can also set it via
 * AdminTenantsService.setTenantTheme, e.g. for support purposes; both
 * write the same tenants.pdf_template column. */
exports.PDF_TEMPLATES = ["classic", "modern"];
/** What kind of business this tenant is — admin-only, never owner/staff-
 * editable. 'auto_repair' is
 * every tenant that exists today; 'detailing' is the first non-vehicle-
 * centric type (see docs/SHOP_TYPES_PLAN.md) — no VIN/YMM picker, no
 * odometer, and global search never returns vehicle results for it. */
exports.SHOP_TYPES = ["auto_repair", "detailing"];
exports.SHOP_TYPE_LABELS = {
    auto_repair: { partLabel: "Part", laborLabel: "Labor", subletLabel: "Sublet" },
    detailing: { partLabel: "Package", laborLabel: "Service", subletLabel: "Sublet" },
};
/**
 * The tenant's own identity fields — name, address, phone, fax — as
 * distinct from `tenant_settings` (tax rates, shop-supplies config, terms
 * text; see tenant-settings.ts). These are what the invoice PDF prints as
 * the shop header (see InvoicesService.issue()'s shopNameSnapshot/
 * shopAddressSnapshot/etc.) — there was no HTTP endpoint for them at all
 * before this; only the owner-run create-tenant.ts CLI script could set
 * them, at provisioning time.
 */
exports.zUpdateShopProfileRequest = zod_1.z
    .object({
    displayName: zod_1.z.string().trim().min(1),
    legalName: zod_1.z.string().trim().min(1),
    // No longer nullable (as of the CAN-SPAM physical-address fix, see
    // docs/GDPR_GAP_ANALYSIS.md and tmp/legal_risk_assessment_2026-09-29.md)
    // — a shop's own address is what prints on the shop→customer email
    // footer, so it can no longer be saved blank. addressLine2 stays
    // optional (apt/suite line, not required for a complete mailing
    // address). Existing tenants that predate this change keep whatever
    // they had (including null) until they next save this section — this
    // only tightens the write path, no migration/backfill.
    addressLine1: zod_1.z.string().trim().min(1),
    addressLine2: zod_1.z.string().trim().min(1).nullable(),
    city: zod_1.z.string().trim().min(1),
    region: zod_1.z.string().trim().min(1),
    postalCode: zod_1.z.string().trim().min(1),
    country: zod_1.z.enum(exports.SUPPORTED_COUNTRY_CODES),
    pdfTemplate: zod_1.z.enum(exports.PDF_TEMPLATES),
    phone: zod_1.z.string().trim().min(1).nullable(),
    fax: zod_1.z.string().trim().min(1).nullable(),
    email: zod_1.z.string().trim().toLowerCase().email().nullable(),
    website: zod_1.z.string().trim().min(1).max(200).nullable(),
    // Whether NEWLY issued invoices/estimates include the logo — read
    // (and, for invoices, snapshotted) at issue time; never retroactive.
    // Uploading/removing the logo itself goes through its own dedicated
    // POST/DELETE /shop-profile/logo endpoints, not this one, since a file
    // upload doesn't fit a JSON PATCH body.
    showLogoOnDocuments: zod_1.z.boolean(),
    // Whether the site header shows the logo next to the shop name — a
    // live display preference, unlike showLogoOnDocuments (never
    // snapshotted anywhere, since the header always reflects the shop's
    // CURRENT identity).
    showLogoInHeader: zod_1.z.boolean(),
    // Whether the header shows the shop NAME — the API rejects a patch
    // that would leave this AND showLogoInHeader both false (see
    // ShopProfileService.update()); a logo-less tenant has nothing else
    // to show, so this can never actually be turned off in that case.
    showShopNameInHeader: zod_1.z.boolean(),
})
    .partial();
//# sourceMappingURL=shop-profile.js.map